/*
 *    Copyright [2007-2025] [wisemapping]
 *
 *   Licensed under WiseMapping Public License, Version 1.0 (the "License").
 *   It is basically the Apache License, Version 2.0 (the "License") plus the
 *   "powered by wisemapping" text requirement on every single page;
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the license at
 *
 *       https://github.com/wisemapping/wisemapping-open-source/blob/main/LICENSE.md
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
import { $defined } from '../utils/assert';
import * as PolyLineUtils from '../utils/PolyLineUtils';
import ElementPeer from './ElementPeer';

class PolyLinePeer extends ElementPeer {
  private _breakDistance: number;

  private _x1: number;

  private _y1: number;

  private _x2: number;

  private _y2: number;

  private _style: string;

  private _orientation: 'horizontal' | 'vertical';

  constructor() {
    const svgElement = window.document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    super(svgElement);
    this.setFill('none');
    this._breakDistance = 10;
    this._x1 = 0;
    this._x2 = 0;
    this._y1 = 0;
    this._y2 = 0;
    this._style = 'Straight';
    this._orientation = 'horizontal';
  }

  setFrom(x1: number, y1: number) {
    this._x1 = x1;
    this._y1 = y1;
    this._updatePath();
  }

  setTo(x2: number, y2: number) {
    this._x2 = x2;
    this._y2 = y2;
    this._updatePath();
  }

  setStrokeWidth(width: number) {
    this._native.setAttribute('stroke-width', String(width));
  }

  setColor(color: string) {
    this._native.setAttribute('stroke', color);
  }

  setStyle(style: string) {
    this._style = style;
    this._updatePath();
  }

  getStyle(): string {
    return this._style;
  }

  setOrientation(orientation: 'horizontal' | 'vertical') {
    this._orientation = orientation;
    this._updatePath();
  }

  getOrientation(): 'horizontal' | 'vertical' {
    return this._orientation;
  }

  private _updatePath() {
    if (this._style === 'Straight') {
      this._updateStraightPath();
    }
    if (this._style === 'MiddleStraight') {
      this._updateMiddleStraightPath();
    } else if (this._style === 'MiddleCurved') {
      this._updateMiddleCurvePath();
    } else if (this._style === 'Curved' || !this._style) {
      this._updateCurvePath();
    }
  }

  private _updateStraightPath() {
    if ($defined(this._x1) && $defined(this._x2) && $defined(this._y1) && $defined(this._y2)) {
      const path =
        this._orientation === 'vertical'
          ? PolyLineUtils.buildVerticalStraightPath(
              this._breakDistance,
              this._x1,
              this._y1,
              this._x2,
              this._y2,
            )
          : PolyLineUtils.buildStraightPath(
              this._breakDistance,
              this._x1,
              this._y1,
              this._x2,
              this._y2,
            );
      this._native.setAttribute('points', path);
    }
  }

  private _updateMiddleCurvePath() {
    const x1 = this._x1;
    const y1 = this._y1;
    const x2 = this._x2;
    const y2 = this._y2;

    if ($defined(x1) && $defined(x2) && $defined(y1) && $defined(y2)) {
      let path: string;
      if (this._orientation === 'vertical') {
        // For vertical tree layout: vertical down, horizontal across, vertical down
        const diff = y2 - y1;
        const middley = diff / 2 + y1;
        let signx = 1;
        if (x2 < x1) {
          signx = -1;
        }
        // Horizontal segment stays at middley (no Y offset for horizontal transition)
        path = `${x1}, ${y1} ${x1}, ${middley.toFixed(0)} ${
          x1 + 10 * signx
        }, ${middley.toFixed(0)} ${x2 - 10 * signx}, ${middley.toFixed(0)} ${x2}, ${middley.toFixed(
          0,
        )} ${x2}, ${y2}`;
      } else {
        // For horizontal mindmap layout
        const diff = x2 - x1;
        const middlex = diff / 2 + x1;
        let signx = 0;
        let signy = 1;
        if (diff < 0) {
          signx = -1;
        }
        if (y2 < y1) {
          signy = -1;
        }
        path = `${x1}, ${y1} ${(middlex - 10 * signx).toFixed(0)}, ${y1} ${middlex.toFixed(
          0,
        )}, ${y1 + 10 * signy} ${middlex}, ${y2 - 10 * signy} ${
          middlex + 10 * signx
        }, ${y2} ${x2}, ${y2}`;
      }
      this._native.setAttribute('points', path);
    }
  }

  private _updateMiddleStraightPath() {
    const x1 = this._x1;
    const y1 = this._y1;
    const x2 = this._x2;
    const y2 = this._y2;
    if ($defined(x1) && $defined(x2) && $defined(y1) && $defined(y2)) {
      let path: string;
      if (this._orientation === 'vertical') {
        // For vertical tree layout: go down, then horizontal, then down
        const diff = y2 - y1;
        const middley = (diff * 0.5 + y1).toFixed(0);
        path = `${x1}, ${y1} ${x1}, ${middley} ${x1}, ${middley} ${x2}, ${middley} ${x2}, ${middley} ${x2}, ${y2}`;
      } else {
        // For horizontal mindmap layout: go horizontal, then vertical, then horizontal
        const diff = x2 - x1;
        const middlex = (diff * 0.5 + x1).toFixed(0);
        path = `${x1}, ${y1} ${middlex}, ${y1} ${middlex}, ${y1} ${middlex}, ${y2} ${middlex}, ${y2} ${x2}, ${y2}`;
      }
      this._native.setAttribute('points', path);
    }
  }

  private _updateCurvePath() {
    if ($defined(this._x1) && $defined(this._x2) && $defined(this._y1) && $defined(this._y2)) {
      const path =
        this._orientation === 'vertical'
          ? PolyLineUtils.buildVerticalCurvedPath(
              this._breakDistance,
              this._x1,
              this._y1,
              this._x2,
              this._y2,
            )
          : PolyLineUtils.buildCurvedPath(
              this._breakDistance,
              this._x1,
              this._y1,
              this._x2,
              this._y2,
            );
      this._native.setAttribute('points', path);
    }
  }
}

export default PolyLinePeer;
